<template>
  <nav v-if="tabs.length" class="bottom-nav" role="navigation" aria-label="Primary">
    <button
      v-for="tab in tabs"
      :key="tab.name"
      class="bn-tab"
      :class="{ 'bn-tab--active': isActive(tab) }"
      :aria-current="isActive(tab) ? 'page' : undefined"
      @click="go(tab)"
    >
      <VaIcon :name="tab.icon" size="22px" />
      <span class="bn-label">{{ t(tab.labelKey) }}</span>
    </button>

    <button
      class="bn-tab"
      :class="{ 'bn-tab--active': moreActive }"
      aria-label="More screens"
      @click="moreOpen = true"
    >
      <VaIcon name="mso-apps" size="22px" />
      <span class="bn-label">More</span>
    </button>

    <MoreSheet v-model="moreOpen" />
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import { BOTTOM_NAV_TABS, type BottomTab } from './bottomNavTabs'
import MoreSheet from './MoreSheet.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const moreOpen = ref(false)

const tabs = computed<BottomTab[]>(() => BOTTOM_NAV_TABS[authStore.user?.role ?? ''] ?? [])

const isActive = (tab: BottomTab) => {
  const current = route.name as string | undefined
  return current === tab.name || (tab.match?.includes(current ?? '') ?? false)
}

// "More" lights up when the current screen isn't one of the primary tabs — i.e. the user is
// somewhere that lives under the full menu.
const moreActive = computed(() => moreOpen.value || !tabs.value.some(isActive))

const go = (tab: BottomTab) => {
  if (route.name !== tab.name) router.push({ name: tab.name })
}
</script>

<style scoped lang="scss">
.bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  align-items: stretch;
  background: var(--va-background-secondary, #fff);
  border-top: 1px solid var(--va-background-border, #e4e9ee);
  padding-bottom: env(safe-area-inset-bottom);
  box-shadow: 0 -6px 20px -14px rgba(0, 0, 0, 0.3);
}
.bn-tab {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 8px 2px 9px;
  color: var(--va-secondary, #75808c);
  transition: color 0.15s ease;
}
.bn-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bn-tab--active {
  color: var(--va-primary, #1d9e75);
}
.bn-tab:active {
  background: var(--va-background-element, #eef2f6);
}
@media (prefers-reduced-motion: reduce) {
  .bn-tab {
    transition: none;
  }
}
</style>
